import { Box, Divider, MenuItem, Select, Tab, Tabs, Theme } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { action } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react'
import React, { useCallback } from 'react'
import { FixedHeader } from '../../../../utils/HOC/FixedHeaders'
import { DocumentFormat } from '../../../Ocr/OcrTypes'
import { OcrCorrectionScreenValue } from '../../Components/Internals/OcrCorrectionScreenConfigurator'
import { FormBuilderEventEditor } from './FormBuilderEventEditor'
import { FormBuilderStyleSection } from './FormBuilderStyleSection'
import { OcrScreenFormBuilderDesigner } from './OcrScreenFormBuilderDesigner'

type OcrScreenDesignerProps = {
	ocrScreenValue: OcrCorrectionScreenValue
}

export const OcrScreenDesigner = observer(
	({ ocrScreenValue }: OcrScreenDesignerProps) => {
		const styles = useStyles()

		const state = useLocalObservable(() => ({
			selectedTabIndex: 0,
		}))

		const handleTabChange = useCallback(
			action((_v: React.SyntheticEvent, newValue: number) => {
				state.selectedTabIndex = newValue
			}),
			[state],
		)

		return (
			<div className={styles.formBuilderTabEditorRoot}>
				<div className={styles.formBuilderTabs}>
					<Tabs
						indicatorColor="primary"
						value={state.selectedTabIndex}
						onChange={handleTabChange}
					>
						<Tab label="Editor"></Tab>
						<Tab label="Events"></Tab>
						<Tab label="Style"></Tab>
					</Tabs>
				</div>
				<Divider />
				{state.selectedTabIndex === 0 && (
					<OcrScreenEditor ocrScreenValue={ocrScreenValue} />
				)}
				{state.selectedTabIndex === 1 && (
					<FormBuilderEventEditor
						schemaReference={ocrScreenValue.formBuilderSchema}
					/>
				)}
				{state.selectedTabIndex === 2 && (
					<FormBuilderStyleSection
						schemaReference={ocrScreenValue.formBuilderSchema}
						onSchemaModified={(schema) => {
							ocrScreenValue.formBuilderSchema = schema
						}}
					/>
				)}
			</div>
		)
	},
)

type OcrScreenEditorProps = {
	ocrScreenValue: OcrCorrectionScreenValue
}

const OcrScreenEditor = observer((props: OcrScreenEditorProps) => {
	return (
		<Box display="flex" flexDirection="column" height="100%">
			<Box display="flex" justifyContent="flex-end">
				<FixedHeader label="Format">
					<Select
						value={props.ocrScreenValue.documentFormat}
						onChange={(evt) =>
							(props.ocrScreenValue.documentFormat = evt.target
								.value as DocumentFormat)
						}
						size="small"
					>
						<MenuItem value={DocumentFormat.Portrait}>Portrait</MenuItem>
						<MenuItem value={DocumentFormat.Landscape}>Landscape</MenuItem>
					</Select>
				</FixedHeader>
			</Box>
			<Box
				display="flex"
				flexDirection={
					props.ocrScreenValue.documentFormat === DocumentFormat.Portrait
						? 'row'
						: 'column'
				}
				height="100%"
			>
				<OcrScreenFormBuilderDesigner
					schemaReference={props.ocrScreenValue.formBuilderSchema}
					onSchemaModified={(schema) => {
						// no-op
					}}
					documentFormat={props.ocrScreenValue.documentFormat}
				/>
			</Box>
		</Box>
	)
})

const useStyles = makeStyles((theme: Theme) => ({
	formBuilderTabEditorRoot: {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
	},
	formBuilderTabs: {
		backgroundColor: theme.palette.background.paper,
	},
}))
